import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  Download,
  Eye,
  Loader2,
  Play,
  RefreshCw,
  Undo2,
} from "lucide-react";
import Select from "react-select";
import toast from "react-hot-toast";
import AttendanceChecklistPanel from "./AttendanceChecklistPanel";
import PayrollActionChooserModal from "../payroll/PayrollActionChooserModal";
import { isEmployeeManualAttendanceProcess } from "../../../lib/attendancePolicyEngine";
import { attendanceService } from "../../../services/api";
import EmpCodeNameSearchBar from "../../Common/EmpCodeNameSearchBar";
import { filterRowsByEmpCodeOrName } from "../../../lib/empCodeNameSearch";
import { ProgressRing } from "../payroll/ProcessPayrollPanel";
import CutoffImpactPanel from "../shared/CutoffImpactPanel";

function empCode(e) {
  return e?.empCode || e?.employeeId || e?.code || e?.id || "—";
}

function empName(e) {
  return (
    e?.user?.name ||
    e?.name ||
    [e?.firstName, e?.lastName].filter(Boolean).join(" ") ||
    "—"
  );
}

function attendanceProcessEmployeeId(employee) {
  const n = Number(employee?.id ?? employee?.employeeId);
  return Number.isFinite(n) && n > 0 ? String(n) : "";
}

function formatPayMonthLabel(payMonth) {
  const s = String(payMonth || "").slice(0, 7);
  const m = s.match(/^(\d{4})-(\d{2})$/);
  if (!m) return s || "—";
  const d = new Date(Number(m[1]), Number(m[2]) - 1, 1);
  return d.toLocaleString("en-IN", { month: "long", year: "numeric" });
}

function previousMonths(payMonth, count = 2) {
  const s = String(payMonth || "").slice(0, 7);
  const m = s.match(/^(\d{4})-(\d{2})$/);
  if (!m) return [];
  let y = Number(m[1]);
  let mo = Number(m[2]); // 1-12
  const out = [];
  for (let i = 0; i < count; i += 1) {
    mo -= 1;
    if (mo < 1) {
      mo = 12;
      y -= 1;
    }
    out.push({ year: y, month: mo }); // month 1-based for API
  }
  return out;
}

function useSelectStyles(darkMode) {
  return useMemo(
    () => ({
      control: (base) => ({
        ...base,
        minHeight: 34,
        fontSize: 12,
        backgroundColor: "hsl(var(--ds-surface))",
        borderColor: darkMode ? "#4C4C5C" : "#d1d5db",
        boxShadow: "none",
      }),
      menu: (base) => ({
        ...base,
        zIndex: 40,
        backgroundColor: "hsl(var(--ds-surface))",
        fontSize: 12,
      }),
      multiValue: (base) => ({
        ...base,
        backgroundColor: darkMode ? "#3a3a3a" : "#fee2e2",
      }),
      multiValueLabel: (base) => ({
        ...base,
        color: darkMode ? "#f3f4f6" : "#991b1b",
        fontSize: 11,
      }),
      input: (base) => ({ ...base, color: darkMode ? "#fff" : "#111" }),
      singleValue: (base) => ({ ...base, color: darkMode ? "#fff" : "#111" }),
      placeholder: (base) => ({ ...base, color: darkMode ? "#9ca3af" : "#6b7280" }),
      option: (base, state) => ({
        ...base,
        backgroundColor: state.isFocused
          ? darkMode
            ? "#3a3a3a"
            : "#f3f4f6"
          : darkMode
            ? "hsl(var(--ds-surface))"
            : "#fff",
        color: darkMode ? "#fff" : "#111",
      }),
    }),
    [darkMode]
  );
}

/** Change Status — Monthly Variables layout (multi-select + apply + log). */
function ChangeStatusStep({
  darkMode,
  payMonth,
  employees = [],
  isLocked,
  editingLocked = false,
  onOpenCalendarQueue,
}) {
  const selectStyles = useSelectStyles(darkMode);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const blocked = !!(isLocked || editingLocked);

  const employeeSelectOptions = useMemo(
    () =>
      (employees || []).map((e) => ({
        value: e.id,
        label: `${empCode(e)} — ${empName(e)}`.trim(),
        employee: e,
      })),
    [employees]
  );

  const openCalendar = () => {
    if (isLocked) {
      toast.error("Month is locked — unlock from Finalise before changing status.");
      return;
    }
    if (editingLocked) {
      toast.error(
        "All employees are processed. Unprocess some before changing status."
      );
      return;
    }
    if (!selectedEmployees.length) {
      toast.error("Select at least one employee");
      return;
    }
    const queue = selectedEmployees.map((o) => o.employee).filter(Boolean);
    if (!queue.length) {
      toast.error("Select at least one employee");
      return;
    }
    onOpenCalendarQueue?.(queue);
  };

  return (
    <div className="space-y-3">
      {isLocked && (
        <p className={`text-xs ${darkMode ? "text-amber-200" : "text-amber-800"}`}>
          Month is locked — unlock from Finalise before changing status.
        </p>
      )}
      {editingLocked && !isLocked && (
        <p className={`text-xs ${darkMode ? "text-amber-200" : "text-amber-800"}`}>
          All employees are processed — Unprocess from Process Attendance to edit status.
        </p>
      )}

      <div className="flex flex-wrap items-start gap-2">
        <div className="min-w-[240px] flex-1">
          <Select
            isMulti
            isSearchable
            isDisabled={blocked || !employees.length}
            options={employeeSelectOptions}
            value={selectedEmployees}
            onChange={(opts) => setSelectedEmployees(opts || [])}
            placeholder="Search & select employees (multi)…"
            styles={selectStyles}
            classNamePrefix="att-cs-emp"
            menuPortalTarget={typeof document !== "undefined" ? document.body : null}
            menuPosition="fixed"
          />
        </div>
        <button
          type="button"
          onClick={openCalendar}
          disabled={blocked || !selectedEmployees.length}
          className="inline-flex items-center justify-center gap-1 rounded bg-[hsl(var(--ds-primary))] px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
        >
          Open calendar
          {selectedEmployees.length > 0 ? ` (${selectedEmployees.length})` : ""}
        </button>
      </div>

      {selectedEmployees.length === 0 ? (
        <div
          className={`rounded-lg border border-dashed px-4 py-10 text-center text-sm ${
            darkMode ? "border-[hsl(var(--ds-border))] text-[hsl(var(--ds-muted-fg))]" : "border-[hsl(var(--ds-border))] text-[hsl(var(--ds-muted-fg))]"
          }`}
        >
          No employees selected for {formatPayMonthLabel(payMonth)}.
        </div>
      ) : (
        <div
          className={`rounded-lg border px-3 py-2 text-xs ${
            darkMode ? "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))]" : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))]"
          }`}
        >
          <p className="mb-1.5 font-semibold">
            Queue ({selectedEmployees.length}) — calendar starts with the first employee
          </p>
          <ol className="max-h-40 list-decimal space-y-0.5 overflow-auto pl-4">
            {selectedEmployees.map((o, i) => (
              <li key={o.value}>
                <span className="font-medium">{o.label}</span>
                {i === 0 ? (
                  <span className="ml-1 text-[10px] text-[hsl(var(--ds-primary))]">(opens first)</span>
                ) : null}
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}

function ApplyLeaveStep({ darkMode, employees, onApplyLeave, editingLocked = false }) {
  const selectStyles = useSelectStyles(darkMode);
  const punchEmployees = useMemo(
    () => (employees || []).filter((e) => !isEmployeeManualAttendanceProcess(e)),
    [employees]
  );
  const options = useMemo(
    () =>
      punchEmployees.map((e) => ({
        value: e.id,
        label: `${empCode(e)} — ${empName(e)}`.trim(),
        employee: e,
      })),
    [punchEmployees]
  );
  const [selected, setSelected] = useState(null);

  return (
    <div className="space-y-3">
      {editingLocked && (
        <p className={`text-xs ${darkMode ? "text-amber-200" : "text-amber-800"}`}>
          All employees are processed — Unprocess from Process Attendance before applying leave.
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        <div className="min-w-[240px] flex-1">
          <Select
            isSearchable
            isDisabled={editingLocked}
            options={options}
            value={selected}
            onChange={setSelected}
            placeholder="Search & select employee…"
            styles={selectStyles}
            classNamePrefix="att-leave-emp"
            menuPortalTarget={typeof document !== "undefined" ? document.body : null}
            menuPosition="fixed"
          />
        </div>
        <button
          type="button"
          disabled={editingLocked || !selected || !onApplyLeave}
          onClick={() => {
            if (editingLocked) {
              toast.error(
                "All employees are processed. Unprocess some before applying leave."
              );
              return;
            }
            selected?.employee && onApplyLeave(selected.employee);
          }}
          className="rounded bg-[hsl(var(--ds-primary))] px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
        >
          Apply Leave
        </button>
      </div>
      {!punchEmployees.length && (
        <div
          className={`rounded-lg border border-dashed px-4 py-8 text-center text-sm ${
            darkMode ? "border-[hsl(var(--ds-border))] text-[hsl(var(--ds-muted-fg))]" : "border-[hsl(var(--ds-border))] text-[hsl(var(--ds-muted-fg))]"
          }`}
        >
          No punch / automated-policy employees in scope.
        </div>
      )}
    </div>
  );
}

/** LOP Reversal — shows only employees with saved LOPR entries tagged to this pay month. */
function LopReversalStep({
  darkMode,
  payMonth,
  employees = [],
  onLopReversal,
  editingLocked = false,
}) {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    if (!payMonth) return;
    setLoading(true);
    try {
      const s = String(payMonth || "").slice(0, 7);
      const m = s.match(/^(\d{4})-(\d{2})$/);
      if (!m) { setRows([]); return; }
      const year = Number(m[1]);
      const month = Number(m[2]);

      const resp = await attendanceService.getLOPReversalReport(month, year);
      const data = Array.isArray(resp?.data) ? resp.data : [];
      const empById = new Map((employees || []).map((e) => [String(e.id), e]));

      const byEmp = new Map();
      data.forEach((r) => {
        const empId = String(r.employeeId || "");
        if (!empId) return;
        const cur = byEmp.get(empId) || {
          employeeId: empId,
          employeeCode: r.employeeCode || "",
          employeeName: r.employeeName || "",
          lopReversalDays: 0,
          lopMonth: "",
          dates: "",
        };
        cur.lopReversalDays += Number(r.lopReversalDays || 0);
        cur.lopMonth = cur.lopMonth
          ? (cur.lopMonth.includes(r.lopMonth) ? cur.lopMonth : `${cur.lopMonth}, ${r.lopMonth}`)
          : (r.lopMonth || "");
        cur.dates = cur.dates ? `${cur.dates}, ${r.dates || ""}` : (r.dates || "");
        byEmp.set(empId, cur);
      });

      const next = Array.from(byEmp.values()).map((r) => ({
        ...r,
        employee: empById.get(r.employeeId) || null,
      }));
      next.sort((a, b) => b.lopReversalDays - a.lopReversalDays);
      setRows(next);
    } catch (err) {
      console.error(err);
      setRows([]);
      toast.error(err?.message || "Failed to load LOP reversal report");
    } finally {
      setLoading(false);
    }
  }, [payMonth, employees]);

  useEffect(() => {
    load();
  }, [load]);

  const filteredRows = useMemo(
    () =>
      filterRowsByEmpCodeOrName(
        rows.map((r) => ({
          ...r,
          empCode: r.employeeCode || empCode(r.employee),
          name: r.employeeName || empName(r.employee),
        })),
        search
      ),
    [rows, search]
  );

  const totalReversedDays = useMemo(
    () => rows.reduce((sum, r) => sum + (Number(r.lopReversalDays) || 0), 0),
    [rows]
  );

  const border = darkMode ? "border-[hsl(var(--ds-border))]" : "border-[hsl(var(--ds-border))]";

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        {/* <p className={`text-[11px] ${darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"}`}>
          LOP reversals saved for {formatPayMonthLabel(payMonth)} payroll.
          {rows.length > 0 && (
            <span className="ml-1 font-semibold">
              {rows.length} employee{rows.length !== 1 ? "s" : ""}, {totalReversedDays} day{totalReversedDays !== 1 ? "s" : ""} reversed.
            </span>
          )}
          {rows.length === 0 && !loading && " Use Actions → LOP Reversal to add entries."}
        </p> */}
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className={`inline-flex items-center gap-1 rounded border px-2 py-1 text-[11px] ${
            darkMode ? "border-[hsl(var(--ds-border))]" : "border-[hsl(var(--ds-border))]"
          }`}
        >
          {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
          Refresh
        </button>
      </div>

      {!loading && rows.length > 0 && (
        <EmpCodeNameSearchBar
          value={search}
          onChange={setSearch}
          darkMode={darkMode}
          showCount
          total={rows.length}
          filtered={filteredRows.length}
        />
      )}

      <div className={`min-h-0 flex-1 overflow-auto rounded-lg border ${border}`}>
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-12 text-xs text-[hsl(var(--ds-muted-fg))]">
            <Loader2 size={14} className="animate-spin" /> Loading LOP reversals…
          </div>
        ) : rows.length === 0 ? (
          <div
            className={`m-3 rounded-lg border border-dashed px-4 py-10 text-center text-sm ${
              darkMode ? "border-[hsl(var(--ds-border))] text-[hsl(var(--ds-muted-fg))]" : "border-[hsl(var(--ds-border))] text-[hsl(var(--ds-muted-fg))]"
            }`}
          >
            No LOP reversals saved for {formatPayMonthLabel(payMonth)}.
          </div>
        ) : filteredRows.length === 0 ? (
          <div
            className={`m-3 rounded-lg border border-dashed px-4 py-10 text-center text-sm ${
              darkMode ? "border-[hsl(var(--ds-border))] text-[hsl(var(--ds-muted-fg))]" : "border-[hsl(var(--ds-border))] text-[hsl(var(--ds-muted-fg))]"
            }`}
          >
            No employees match “{search.trim()}”.
          </div>
        ) : (
          <table className="w-full text-xs">
            <thead className={`sticky top-0 z-10 ${darkMode ? "bg-[hsl(var(--ds-bg))]" : "bg-[hsl(var(--ds-muted))]"}`}>
              <tr>
                <th className="px-2 py-2 text-left font-semibold">Sl. No</th>
                <th className="px-2 py-2 text-left font-semibold">Emp ID</th>
                <th className="px-2 py-2 text-left font-semibold">Name</th>
                <th className="px-2 py-2 text-right font-semibold">Reversed Days</th>
                <th className="px-2 py-2 text-left font-semibold">LOP Month</th>
                <th className="px-2 py-2 text-left font-semibold">Dates</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((r, i) => (
                <tr
                  key={r.employeeId}
                  className={`border-t ${darkMode ? "border-[hsl(var(--ds-border))]" : "border-[hsl(var(--ds-border))]"}`}
                >
                  <td className="px-2 py-1.5 tabular-nums text-[hsl(var(--ds-muted-fg))]">{i + 1}</td>
                  <td className="px-2 py-1.5 font-medium">{r.employeeCode || empCode(r.employee)}</td>
                  <td className="px-2 py-1.5">{r.employeeName || empName(r.employee)}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums font-semibold">
                    {r.lopReversalDays}
                  </td>
                  <td className="px-2 py-1.5 text-[10px] text-[hsl(var(--ds-muted-fg))]">
                    {r.lopMonth}
                  </td>
                  <td className="px-2 py-1.5 text-[10px] text-[hsl(var(--ds-muted-fg))]">
                    {r.dates}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function ReconciliationStep({
  darkMode,
  employees = [],
  getStatusCounts,
  leaveCodes = [],
  editingLocked = false,
  payMonth = "",
}) {
  const [tab, setTab] = useState("lops");
  const [search, setSearch] = useState("");
  const border = darkMode ? "border-[hsl(var(--ds-border))]" : "border-[hsl(var(--ds-border))]";

  const leaveCodeSet = useMemo(() => {
    const set = new Set(
      (leaveCodes || [])
        .map((c) => String(c || "").trim().toUpperCase())
        .filter((c) => c && c !== "LOP" && c !== "LWP" && c !== "PR" && c !== "PH" && c !== "WO")
    );
    return set;
  }, [leaveCodes]);

  const rows = useMemo(() => {
    if (tab === "cutoff") return [];
    return (employees || [])
      .map((e) => {
        const counts =
          (typeof getStatusCounts === "function" ? getStatusCounts(e.id) : {}) || {};
        let leaveTotal = 0;
        const leaveBreakdown = [];
        Object.entries(counts).forEach(([code, val]) => {
          const upper = String(code || "").toUpperCase();
          const n = Number(val) || 0;
          if (n <= 0) return;
          if (leaveCodeSet.has(upper)) {
            leaveTotal += n;
            leaveBreakdown.push({ code: upper, n });
          }
        });
        // LOP status only (not leave types)
        let lop = 0;
        Object.entries(counts).forEach(([code, val]) => {
          const upper = String(code || "").toUpperCase();
          const n = Number(val) || 0;
          if (n <= 0) return;
          if (
            upper === "LOP" ||
            upper === "LWP" ||
            upper === "LOP/PR" ||
            upper === "PR/LOP" ||
            /^LOP\//.test(upper) ||
            /\/LOP$/.test(upper)
          ) {
            // half-day LOP composites: count as 0.5 each unit already stored as days
            lop += n;
          }
        });
        return { e, leaveTotal, leaveBreakdown, lop };
      })
      .filter((r) => (tab === "leaves" ? r.leaveTotal > 0 : r.lop > 0));
  }, [employees, getStatusCounts, leaveCodeSet, tab]);

  const filteredRows = useMemo(
    () =>
      filterRowsByEmpCodeOrName(
        rows.map((r) => ({
          ...r,
          empCode: empCode(r.e),
          name: empName(r.e),
        })),
        search
      ),
    [rows, search]
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      {editingLocked && (
        <p className={`text-xs ${darkMode ? "text-amber-200" : "text-amber-800"}`}>
          All employees are processed — review only. Unprocess from Process Attendance to
          re-validate this step after data changes.
        </p>
      )}
      <div className="flex shrink-0 flex-nowrap items-center gap-2 overflow-x-auto">
        <div className="flex shrink-0 gap-1">
          {[
            { id: "lops", label: "LOPs" },
            { id: "leaves", label: "Leaves" },
            { id: "cutoff", label: "Cut-off" },
          ].map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={`rounded-full px-3 py-1 text-xs font-semibold ${
                tab === t.id
                  ? "bg-[hsl(var(--ds-primary))] text-white"
                  : darkMode
                  ? "bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-muted-fg))]"
                  : "bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-fg))]"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        {tab !== "cutoff" && rows.length > 0 ? (
          <EmpCodeNameSearchBar
            value={search}
            onChange={setSearch}
            darkMode={darkMode}
            showCount
            total={rows.length}
            filtered={filteredRows.length}
            className="min-w-[180px] flex-1"
          />
        ) : null}
      </div>

      {tab === "cutoff" ? (
        <CutoffImpactPanel payMonth={payMonth} darkMode={darkMode} className="min-h-0 flex-1" />
      ) : (
      <div className={`min-h-0 flex-1 overflow-auto rounded-lg border ${border}`}>
        <table className="w-full text-xs">
          <thead className={`sticky top-0 ${darkMode ? "bg-[hsl(var(--ds-bg))]" : "bg-[hsl(var(--ds-muted))]"}`}>
            <tr>
              <th className="px-2 py-2 text-left font-semibold">Sl. No</th>
              <th className="px-2 py-2 text-left font-semibold">Emp ID</th>
              <th className="px-2 py-2 text-left font-semibold">Name</th>
              <th className="px-2 py-2 text-right font-semibold">
                {tab === "leaves" ? "Leave days" : "LOP days"}
              </th>
              {tab === "leaves" && (
                <th className="px-2 py-2 text-left font-semibold">Breakdown</th>
              )}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td
                  colSpan={tab === "leaves" ? 5 : 4}
                  className={`px-3 py-8 text-center ${
                    darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"
                  }`}
                >
                  {tab === "leaves"
                    ? "No leave types applied for this month."
                    : "No LOP status days for this month."}
                </td>
              </tr>
            ) : filteredRows.length === 0 ? (
              <tr>
                <td
                  colSpan={tab === "leaves" ? 5 : 4}
                  className={`px-3 py-8 text-center ${
                    darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"
                  }`}
                >
                  No employees match “{search.trim()}”.
                </td>
              </tr>
            ) : (
              filteredRows.map((r, i) => (
                <tr
                  key={r.e.id}
                  className={`border-t ${darkMode ? "border-[hsl(var(--ds-border))]" : "border-[hsl(var(--ds-border))]"}`}
                >
                  <td className="px-2 py-1.5 tabular-nums text-[hsl(var(--ds-muted-fg))]">{i + 1}</td>
                  <td className="px-2 py-1.5 font-medium">{empCode(r.e)}</td>
                  <td className="px-2 py-1.5">{empName(r.e)}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums font-semibold">
                    {tab === "leaves" ? r.leaveTotal : r.lop}
                  </td>
                  {tab === "leaves" && (
                    <td className="px-2 py-1.5 text-[10px] text-[hsl(var(--ds-muted-fg))]">
                      {r.leaveBreakdown.map((b) => `${b.code}:${b.n}`).join(" · ")}
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      )}
    </div>
  );
}

/** Process Attendance — Process Payroll layout (badges, table, footer actions via parent). */
export function ProcessAttendanceActions({
  darkMode,
  unprocessedCount = 0,
  processedCount = 0,
  stepBusy,
  onOpenAction,
  isLocked = false,
}) {
  const blocked = stepBusy || isLocked;
  const canProcess = unprocessedCount > 0 && !isLocked;
  const canReprocess = processedCount > 0 && !isLocked;
  const canUnprocess = processedCount > 0 && !isLocked;
  const hasAnyProcessed = processedCount > 0;

  return (
    <>
      <button
        type="button"
        disabled={blocked || !canProcess}
        title={
          isLocked
            ? "Month locked"
            : unprocessedCount === 0
            ? "No unprocessed employees"
            : "Choose who to process"
        }
        onClick={() => onOpenAction?.("process")}
        className="inline-flex items-center gap-1 rounded bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
      >
        <Play size={14} />
        Process ({unprocessedCount})
      </button>
      {hasAnyProcessed && (
        <>
          <button
            type="button"
            disabled={blocked || !canReprocess}
            title="Re-sync attendance for processed employees — they stay processed (Change Status stays locked)"
            onClick={() => onOpenAction?.("reprocess")}
            className={`inline-flex items-center gap-1 rounded border px-3 py-1.5 text-xs disabled:opacity-50 ${
              darkMode ? "border-[hsl(var(--ds-border))]" : "border-[hsl(var(--ds-border))]"
            }`}
          >
            <RefreshCw size={14} />
            Reprocess ({processedCount})
          </button>
          <button
            type="button"
            disabled={blocked || !canUnprocess}
            title="Clear processed mark — unlocks Change Status / Leave / LOP / Reconciliation"
            onClick={() => onOpenAction?.("unprocess")}
            className={`inline-flex items-center gap-1 rounded border px-3 py-1.5 text-xs disabled:opacity-50 ${
              darkMode ? "border-[hsl(var(--ds-border))]" : "border-[hsl(var(--ds-border))]"
            }`}
          >
            <Undo2 size={14} />
            Unprocess ({processedCount})
          </button>
        </>
      )}
    </>
  );
}

/** Footer actions for Finalise Attendance (locks the month). */
export function FinaliseAttendanceActions({
  darkMode,
  stepBusy = false,
  isLocked = false,
  stepsFullyLocked = false,
  processedCount = 0,
  totalCount = 0,
  canFinalise = true,
  onFinalise,
  onRequestUnlock,
  workflowMethod = "passkey",
  pendingLockRequest = false,
  pendingUnlockRequest = false,
}) {
  if (isLocked) {
    return (
      <>
        <span className="rounded-full bg-emerald-100 px-2.5 py-1 text-[10px] font-semibold uppercase text-emerald-800">
          Finalised / Locked
        </span>
        {pendingUnlockRequest ? (
          <span className="rounded-full bg-amber-100 px-2.5 py-1 text-[10px] font-semibold text-amber-800">
            Unlock Request Pending Approval
          </span>
        ) : typeof onRequestUnlock === "function" ? (
          <button
            type="button"
            disabled={stepBusy}
            onClick={onRequestUnlock}
            className={`inline-flex items-center gap-1 rounded border px-3 py-1.5 text-xs font-medium disabled:opacity-50 ${
              darkMode ? "border-[hsl(var(--ds-border))] text-[hsl(var(--ds-fg))]" : "border-[hsl(var(--ds-border))] text-[hsl(var(--ds-fg))]"
            }`}
          >
            {workflowMethod === "auto_approved" ? "Unlock Attendance" : "Request unlock"}
          </button>
        ) : null}
      </>
    );
  }

  if (!canFinalise) {
    return (
      <span className={`text-[11px] ${darkMode ? "text-amber-200" : "text-amber-800"}`}>
        No permission to lock this month
      </span>
    );
  }

  if (!stepsFullyLocked) {
    return (
      <span className={`text-[11px] ${darkMode ? "text-amber-200" : "text-amber-800"}`}>
        Process all company employees first ({processedCount}/{totalCount || "—"} processed)
      </span>
    );
  }

  if (pendingLockRequest) {
    return (
      <span className="rounded-full bg-amber-100 px-2.5 py-1 text-[10px] font-semibold text-amber-800">
        Lock Request Pending Approval
      </span>
    );
  }

  return (
    <button
      type="button"
      disabled={stepBusy}
      onClick={() => onFinalise?.()}
      className="inline-flex items-center gap-1 rounded bg-[hsl(var(--ds-primary))] px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
      title={
        workflowMethod === "workflow"
          ? "Submit attendance lock request for approval"
          : "Lock / finalise this attendance month"
      }
    >
      {stepBusy ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
      {workflowMethod === "workflow" ? "Request Finalisation" : "Finalise"}
    </button>
  );
}

function ProcessAttendanceStep({
  darkMode,
  payMonth,
  employees = [],
  statusColumns = [],
  getStatusCounts,
  getPayDays,
  processCountsRevision = 0,
  isLocked,
  onExportExcel,
  policies = [],
  scopeMode,
  selectedPolicyIds,
  onScopeChange,
  onOpenCalendar,
  processedEmployeeIds = [],
  totalCount = 0,
  batchProgress = null,
}) {
  const [exporting, setExporting] = useState(false);
  const [search, setSearch] = useState("");
  const [listTab, setListTab] = useState("processed");
  const processedSet = useMemo(
    () => new Set((processedEmployeeIds || []).map(String)),
    [processedEmployeeIds]
  );

  const processedEmployees = useMemo(
    () =>
      (employees || []).filter((e) => {
        const id = attendanceProcessEmployeeId(e);
        return id && processedSet.has(id);
      }),
    [employees, processedSet]
  );
  const unprocessedEmployees = useMemo(
    () =>
      (employees || []).filter((e) => {
        const id = attendanceProcessEmployeeId(e);
        return !id || !processedSet.has(id);
      }),
    [employees, processedSet]
  );
  const listEmployees =
    listTab === "unprocessed" ? unprocessedEmployees : processedEmployees;
  const filteredList = useMemo(
    () =>
      filterRowsByEmpCodeOrName(
        listEmployees.map((e) => ({
          ...e,
          empCode: empCode(e),
          name: empName(e),
        })),
        search
      ),
    [listEmployees, search]
  );
  const unprocessedCount = unprocessedEmployees.length;
  const processedCount = processedEmployees.length;
  const displayTotal = totalCount > 0 ? totalCount : (employees || []).length;

  const border = darkMode ? "border-[hsl(var(--ds-border))]" : "border-[hsl(var(--ds-border))]";
  const cols = statusColumns.length
    ? statusColumns
    : ["PR", "PH", "WO", "LOP", "Pay Days"];

  const handleExport = async () => {
    if (typeof onExportExcel !== "function") {
      toast.error("Export is not available");
      return;
    }
    setExporting(true);
    try {
      await onExportExcel();
    } catch (err) {
      toast.error(err?.message || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      {isLocked && (
        <p
          className={`rounded-lg border px-3 py-2 text-xs ${
            darkMode
              ? "border-emerald-800/50 bg-emerald-950/30 text-emerald-100"
              : "border-emerald-200 bg-emerald-50 text-emerald-900"
          }`}
        >
          This month is locked / finalised. Unlock from the Finalise step before processing
          changes.
        </p>
      )}

      <div className="flex flex-wrap items-end justify-between gap-2">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label
              className={`mb-1 block text-[10px] font-semibold uppercase ${
                darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"
              }`}
            >
              Process scope
            </label>
            <select
              value={scopeMode || "all"}
              onChange={(e) =>
                onScopeChange?.({
                  scopeMode: e.target.value,
                  selectedPolicyIds:
                    e.target.value === "all" ? [] : selectedPolicyIds || [],
                })
              }
              className={`rounded-lg border px-2 py-1.5 text-xs outline-none focus:border-[hsl(var(--ds-primary))] ${
                darkMode
                  ? "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] text-white"
                  : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))]"
              }`}
            >
              <option value="all">All policies (default)</option>
              <option value="selected">Selected policies</option>
            </select>
          </div>
          {scopeMode === "selected" && (
            <div className="max-w-md">
              <label
                className={`mb-1 block text-[10px] font-semibold uppercase ${
                  darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"
                }`}
              >
                Policies
              </label>
              <div
                className={`flex max-h-20 flex-wrap gap-1 overflow-auto rounded-lg border p-1.5 ${border}`}
              >
                {(policies || []).map((p) => {
                  const id = String(p.id ?? p.policyId ?? p.name);
                  const checked = (selectedPolicyIds || []).map(String).includes(id);
                  return (
                    <label
                      key={id}
                      className={`inline-flex cursor-pointer items-center gap-1 rounded-full px-2 py-0.5 text-[10px] ${
                        checked
                          ? "bg-[hsl(var(--ds-primary))] text-white"
                          : darkMode
                          ? "bg-[#333] text-[hsl(var(--ds-muted-fg))]"
                          : "bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-fg))]"
                      }`}
                    >
                      <input
                        type="checkbox"
                        className="sr-only"
                        checked={checked}
                        onChange={() => {
                          const cur = (selectedPolicyIds || []).map(String);
                          const next = checked
                            ? cur.filter((x) => x !== id)
                            : [...cur, id];
                          onScopeChange?.({
                            scopeMode: "selected",
                            selectedPolicyIds: next,
                          });
                        }}
                      />
                      {p.name || p.policyName || id}
                    </label>
                  );
                })}
                {!policies?.length && (
                  <span className="text-[10px] text-[hsl(var(--ds-muted-fg))]">No policies found</span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 text-[11px]">
        <button
          type="button"
          onClick={() => setListTab("unprocessed")}
          className={`rounded border px-2 py-1 ${
            listTab === "unprocessed"
              ? "border-emerald-600 bg-emerald-50 font-semibold text-emerald-900"
              : border
          }`}
        >
          Unprocessed <strong className="tabular-nums">{unprocessedCount}</strong>
        </button>
        <button
          type="button"
          onClick={() => setListTab("processed")}
          className={`rounded border px-2 py-1 ${
            listTab === "processed"
              ? "border-emerald-600 bg-emerald-50 font-semibold text-emerald-900"
              : border
          }`}
        >
          Processed <strong className="tabular-nums">{processedCount}</strong>
        </button>
        {listEmployees.length > 0 && (
          <EmpCodeNameSearchBar
            value={search}
            onChange={setSearch}
            darkMode={darkMode}
            showCount
            total={listEmployees.length}
            filtered={filteredList.length}
            className="min-w-[180px] flex-1"
          />
        )}
        <div className="ml-auto">
          <button
            type="button"
            disabled={exporting}
            onClick={handleExport}
            className={`inline-flex items-center gap-1 rounded border px-2.5 py-1.5 text-xs font-medium disabled:opacity-50 ${
              darkMode ? "border-[hsl(var(--ds-border))] text-[hsl(var(--ds-fg))]" : "border-[hsl(var(--ds-border))] text-[hsl(var(--ds-fg))]"
            }`}
            title="Quick reports — attendance register Excel (day-to-day + totals)"
          >
            {exporting ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <Download size={14} />
            )}
            Quick reports
          </button>
        </div>
      </div>

      {batchProgress &&
        (batchProgress.action === "process" ||
          batchProgress.action === "reprocess" ||
          batchProgress.action === "unprocess") && (
        <div className={`flex flex-wrap justify-center gap-8 rounded-lg border p-4 ${border}`}>
          {(batchProgress.action === "process" ||
            batchProgress.action === "reprocess") && (
            <ProgressRing
              darkMode={darkMode}
              done={batchProgress.done}
              total={batchProgress.total}
              label={
                batchProgress.action === "reprocess" ? "Reprocessing" : "Processing"
              }
              color="#059669"
            />
          )}
          {batchProgress.action === "unprocess" && (
            <ProgressRing
              darkMode={darkMode}
              done={batchProgress.done}
              total={batchProgress.total}
              label="Unprocessing"
              color="#dc2626"
            />
          )}
          <p
            className={`w-full text-center text-xs ${
              darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"
            }`}
          >
            {batchProgress.done} of {batchProgress.total} employee(s) completed
            {batchProgress.done < batchProgress.total ? " — batches running in parallel" : ""}
          </p>
        </div>
      )}

      <div className={`min-h-0 flex-1 overflow-auto rounded-lg border ${border}`}>
        {listEmployees.length === 0 ? (
          <p
            className={`rounded-lg border border-dashed m-3 px-4 py-8 text-center text-sm ${
              darkMode ? "border-[hsl(var(--ds-border))] text-[hsl(var(--ds-muted-fg))]" : "border-[hsl(var(--ds-border))] text-[hsl(var(--ds-muted-fg))]"
            }`}
          >
            {listTab === "unprocessed"
              ? "All in-scope employees are processed."
              : "No processed employees yet. Use Process in the footer to confirm attendance for this month."}
          </p>
        ) : filteredList.length === 0 ? (
          <p
            className={`rounded-lg border border-dashed m-3 px-4 py-8 text-center text-sm ${
              darkMode ? "border-[hsl(var(--ds-border))] text-[hsl(var(--ds-muted-fg))]" : "border-[hsl(var(--ds-border))] text-[hsl(var(--ds-muted-fg))]"
            }`}
          >
            No employees match “{search.trim()}”.
          </p>
        ) : (
          <table
            key={`process-counts-${processCountsRevision}-${listTab}`}
            className="w-full min-w-[720px] text-xs"
          >
            <thead
              className={`sticky top-0 z-10 ${darkMode ? "bg-[hsl(var(--ds-bg))]" : "bg-[hsl(var(--ds-muted))]"}`}
            >
              <tr>
                <th className="px-2 py-2 text-left font-bold whitespace-nowrap">Sl. No</th>
                <th className="px-2 py-2 text-left font-bold whitespace-nowrap">Emp ID</th>
                <th className="px-2 py-2 text-left font-bold whitespace-nowrap">Name</th>
                {cols.map((c) => (
                  <th
                    key={c}
                    className="px-2 py-2 text-right font-bold whitespace-nowrap"
                  >
                    {c}
                  </th>
                ))}
                <th className="px-2 py-2 text-center font-bold">View</th>
              </tr>
            </thead>
            <tbody>
              {filteredList.map((e, i) => {
                const counts =
                  (typeof getStatusCounts === "function" ? getStatusCounts(e.id) : {}) ||
                  {};
                return (
                  <tr
                    key={e.id}
                    className={`border-t ${
                      darkMode ? "border-[hsl(var(--ds-border))]" : "border-[hsl(var(--ds-border))]"
                    } ${
                      listTab === "processed"
                        ? darkMode
                          ? "bg-emerald-950/10"
                          : "bg-emerald-50/30"
                        : ""
                    }`}
                  >
                    <td className="px-2 py-1.5 tabular-nums text-[hsl(var(--ds-muted-fg))]">{i + 1}</td>
                    <td className="px-2 py-1.5 font-medium">{empCode(e)}</td>
                    <td className="px-2 py-1.5">{empName(e)}</td>
                    {cols.map((c) => {
                      const key = String(c);
                      let val;
                      if (/pay\s*days/i.test(key)) {
                        val =
                          typeof getPayDays === "function"
                            ? getPayDays(e.id)
                            : counts.payDays ?? "—";
                      } else {
                        val =
                          counts[key] ??
                          counts[key.toUpperCase()] ??
                          counts[key.toLowerCase()] ??
                          0;
                      }
                      return (
                        <td key={c} className="px-2 py-1.5 text-right tabular-nums">
                          {val}
                        </td>
                      );
                    })}
                    <td className="px-2 py-1.5 text-center">
                      <button
                        type="button"
                        onClick={() => onOpenCalendar?.(e)}
                        className="inline-flex text-[hsl(var(--ds-primary))]"
                        title="Open calendar"
                      >
                        <Eye size={14} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
function PreviewValidateStep({
  darkMode,
  payMonth,
  employees = [],
  isLocked,
  processedCount = 0,
  totalCount = 0,
  includeApplyLeave,
  getStatusCounts,
  leaveCodes = [],
}) {
  const punchEmployees = employees.filter((e) => !isEmployeeManualAttendanceProcess(e));
  const leaveCodeSet = new Set(
    (leaveCodes || []).map((c) => String(c).toUpperCase()).filter(Boolean)
  );

  let leaveHeadcount = 0;
  let lopHeadcount = 0;
  employees.forEach((e) => {
    const counts =
      (typeof getStatusCounts === "function" ? getStatusCounts(e.id) : {}) || {};
    let leaveTotal = 0;
    Object.entries(counts).forEach(([code, val]) => {
      const upper = String(code || "").toUpperCase();
      const n = Number(val) || 0;
      if (n > 0 && leaveCodeSet.has(upper) && upper !== "LOP" && upper !== "LWP") {
        leaveTotal += n;
      }
    });
    if (leaveTotal > 0) leaveHeadcount += 1;
    if (Number(counts.LOP || 0) > 0) lopHeadcount += 1;
  });

  const cards = [
    {
      title: "Change Status",
      ok: true,
      detail: `${employees.length} employee(s) in scope for ${payMonth}`,
    },
    includeApplyLeave
      ? {
          title: "Apply Leave",
          ok: true,
          detail: `${punchEmployees.length} punch / automated employee(s) eligible`,
        }
      : null,
    {
      title: "LOP Reversal",
      ok: true,
      detail: "Prior 2-month LOP list available for reversal into this month",
    },
    {
      title: "Reconciliation",
      ok: true,
      detail: `Leaves: ${leaveHeadcount} · LOPs: ${lopHeadcount}`,
    },
    {
      title: "Process Attendance",
      ok: totalCount > 0 && processedCount >= totalCount,
      detail: `${processedCount}/${totalCount || employees.length} company employees processed`,
    },
    {
      title: "Finalise readiness",
      ok: !!isLocked,
      detail: isLocked ? "Month locked" : "Not locked yet — use Finalise step",
    },
  ].filter(Boolean);

  if (punchEmployees.length > 0) {
    cards.push({
      title: "Exceptions / Missing punches",
      ok: true,
      detail: `${punchEmployees.length} automated-policy employee(s) — review open sessions & regularisation on calendar if needed`,
      warn: true,
    });
  }

  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {cards.map((c) => (
        <div
          key={c.title}
          className={`rounded-lg border px-3 py-3 bg-transparent ${
            c.warn || !c.ok ? "border-amber-400/70" : "border-emerald-400/70"
          }`}
        >
          <div className="mb-1 flex items-center gap-1.5">
            {c.warn || !c.ok ? (
              <AlertTriangle size={14} className="text-amber-600" />
            ) : (
              <CheckCircle2 size={14} className="text-emerald-600" />
            )}
            <p className="text-xs font-semibold">{c.title}</p>
          </div>
          <p className={`text-[11px] ${darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"}`}>
            {c.detail}
          </p>
        </div>
      ))}
    </div>
  );
}

function FinaliseStep({ darkMode, isLocked, finalisedAt, stepsFullyLocked, processedCount, totalCount, onRequestUnlock }) {
  return (
    <div className={`text-sm ${darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-fg))]"}`}>
      {isLocked || finalisedAt ? (
        <div className="flex flex-wrap items-center gap-2">
          <span className="rounded-full bg-emerald-100 px-2.5 py-1 text-[10px] font-semibold uppercase text-emerald-800">
            {finalisedAt ? "Finalised" : "Month locked"}
          </span>
          {typeof onRequestUnlock === "function" && (
            <button
              type="button"
              onClick={onRequestUnlock}
              className="text-xs font-medium text-[hsl(var(--ds-primary))] underline"
            >
              Request unlock
            </button>
          )}
        </div>
      ) : (
        <p className="text-xs text-amber-700">
          {stepsFullyLocked ? (
            <>
              Click <strong>Finalise</strong> in the footer to lock this attendance month.
            </>
          ) : (
            <>
              Process all company employees before finalising ({processedCount}/{totalCount || "—"}{" "}
              processed).
            </>
          )}
        </p>
      )}
    </div>
  );
}

export default function ProcessAttendanceStepBody({
  stepKey,
  darkMode,
  payMonth,
  employees,
  isLocked,
  editingLocked = false,
  stepsFullyLocked = false,
  serverFullyLocked = false,
  finalisedAt,
  includeApplyLeave,
  statusColumns,
  leaveCodes,
  getStatusCounts,
  getPayDays,
  processCountsRevision = 0,
  policies,
  scopeMode,
  selectedPolicyIds,
  onScopeChange,
  onOpenCalendar,
  onOpenCalendarQueue,
  onApplyLeave,
  onLopReversal,
  onExportExcel,
  onRequestUnlock,
  processedCount,
  totalCount = 0,
  processedEmployeeIds = [],
  batchProgress = null,
}) {
  if (stepKey === "change_status") {
    return (
      <ChangeStatusStep
        darkMode={darkMode}
        payMonth={payMonth}
        employees={employees}
        isLocked={isLocked}
        editingLocked={editingLocked}
        onOpenCalendarQueue={onOpenCalendarQueue || ((queue) => onOpenCalendar?.(queue?.[0]))}
      />
    );
  }
  if (stepKey === "apply_leave") {
    return (
      <ApplyLeaveStep
        darkMode={darkMode}
        employees={employees}
        onApplyLeave={onApplyLeave}
        editingLocked={editingLocked}
      />
    );
  }
  if (stepKey === "lop_reversal") {
    return (
      <LopReversalStep
        darkMode={darkMode}
        payMonth={payMonth}
        employees={employees}
        onLopReversal={onLopReversal}
        editingLocked={editingLocked}
      />
    );
  }
  if (stepKey === "reconciliation") {
    return (
      <ReconciliationStep
        darkMode={darkMode}
        employees={employees}
        getStatusCounts={getStatusCounts}
        leaveCodes={leaveCodes}
        editingLocked={editingLocked}
        payMonth={payMonth}
      />
    );
  }
  if (stepKey === "process") {
    return (
      <ProcessAttendanceStep
        darkMode={darkMode}
        payMonth={payMonth}
        employees={employees}
        statusColumns={statusColumns}
        getStatusCounts={getStatusCounts}
        getPayDays={getPayDays}
        processCountsRevision={processCountsRevision}
        isLocked={isLocked}
        onExportExcel={onExportExcel}
        policies={policies}
        scopeMode={scopeMode}
        selectedPolicyIds={selectedPolicyIds}
        onScopeChange={onScopeChange}
        onOpenCalendar={onOpenCalendar}
        processedEmployeeIds={processedEmployeeIds}
        totalCount={totalCount}
        batchProgress={batchProgress}
      />
    );
  }
  if (stepKey === "checklist") {
    return (
      <AttendanceChecklistPanel
        darkMode={darkMode}
        payMonth={payMonth}
        readOnly={!stepsFullyLocked || !!isLocked}
      />
    );
  }
  if (stepKey === "preview_validate") {
    return (
      <PreviewValidateStep
        darkMode={darkMode}
        payMonth={payMonth}
        employees={employees}
        isLocked={isLocked}
        processedCount={processedCount}
        includeApplyLeave={includeApplyLeave}
        getStatusCounts={getStatusCounts}
        leaveCodes={leaveCodes}
        totalCount={totalCount}
      />
    );
  }
  if (stepKey === "finalise") {
    return (
      <FinaliseStep
        darkMode={darkMode}
        isLocked={isLocked}
        finalisedAt={finalisedAt}
        stepsFullyLocked={!!serverFullyLocked}
        processedCount={processedCount}
        totalCount={totalCount}
        onRequestUnlock={onRequestUnlock}
      />
    );
  }
  return (
    <p className={`text-xs ${darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"}`}>Select a step.</p>
  );
}

export { PayrollActionChooserModal };
